/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useMemo } from 'react';
import propTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { fetchConfiguration, fetchPropertyManagement, newPropertyManagement,
         renewPropertyManagement, cancelPropertyManagement } from '../../../misc/apiRequests';
import Checkbox from '../../../presentational/checkbox';
import Checkmark from '../../../../assets/images/payments/check-solid.svg';
import Swal from 'sweetalert2';
import { MANAGEMENT_PLANS, planByKey, AGREEMENT_SECTIONS, AGREEMENT_VERSION,
         YARD_FREQUENCIES, INSPECTION_FREQUENCIES, INSPECTION_DAYS,
         BILL_TYPES, BILLING_CYCLES, ACCESS_METHODS } from './managementPlans';
import '../../../../assets/css/management.css';

const STEP_PLAN = 1;
const STEP_AGREEMENT = 2;
const STEP_CARE = 3;
const STEP_REVIEW = 4;

const PropertyManagement = ({ property, user, handleLoader }) => {
  const { t } = useTranslation();

  const [enrolment, setEnrolment] = useState(null);
  const [config, setConfig] = useState({});
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(STEP_PLAN);
  const [submitting, setSubmitting] = useState(false);

  // Step 1 - plan
  const [plan, setPlan] = useState('');
  const [billAddon, setBillAddon] = useState(false);

  // Step 2 - agreement
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [signature, setSignature] = useState('');
  const [agreed, setAgreed] = useState(false);

  // Step 3 - property care
  const [yardFrequency, setYardFrequency] = useState('as needed');
  const [yardNotes, setYardNotes] = useState('');
  const [inspectionFrequency, setInspectionFrequency] = useState('quarterly');
  const [inspectionDay, setInspectionDay] = useState('monday');
  const [bills, setBills] = useState([]);
  const [billsOther, setBillsOther] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [approvedVendors, setApprovedVendors] = useState(true);
  const [accessMethod, setAccessMethod] = useState('drop off keys');
  const [accessDetail, setAccessDetail] = useState('');

  const propertyAddress = [property?.street_address, property?.settlement, property?.island]
    .filter(Boolean).join(', ');

  // api_configs holds the live prices; the plan file only carries fallbacks so a
  // price change never needs a frontend deploy.
  const priceOf = (field, fallback) => {
    const value = field ? Number(config[field]) : NaN;
    return Number.isFinite(value) && value > 0 ? value : fallback;
  };

  const selectedPlan = planByKey(plan);

  const monthlyDue = useMemo(() => {
    if (!selectedPlan) return 0;
    const base = priceOf(selectedPlan.monthlyCostField, selectedPlan.monthlyCostFallback);
    const addon = (selectedPlan.allowsBillAddon && billAddon)
      ? priceOf(selectedPlan.billAddonField, selectedPlan.billAddonFallback)
      : 0;

    return base + addon;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan, billAddon, config]);

  const errorToast = errors => Swal.fire({
    position: 'middle',
    icon: 'error',
    title: Array.isArray(errors) ? errors.join("\n") : errors,
    showConfirmButton: true
  });

  useEffect(() => {
    fetchConfiguration()
      .then(response => { if (response.success) setConfig(response.record || {}); });
  }, []);

  useEffect(() => {
    if (!property?.id) return;

    fetchPropertyManagement(property.id)
      .then(response => {
        // A property with nothing set up answers 404, which is not an error here
        if (response.success) setEnrolment(response.record || null);
      });
  }, [property?.id]);

  // Prefill the agreement from the owner's profile - they can still edit it
  useEffect(() => {
    setClientName(current => current || [user?.first_name, user?.last_name].filter(Boolean).join(' '));
    setClientEmail(current => current || user?.email || '');
    setClientPhone(current => current || user?.phone_numb || user?.mobile_numb || '');
  }, [user]);

  const toggleBill = billName => setBills(current => (
    current.includes(billName) ? current.filter(b => b !== billName) : [...current, billName]
  ));

  const handleNext = () => {
    if (step === STEP_PLAN && !plan) return errorToast(t('f_mgmtPickPlan'));
    if (step === STEP_AGREEMENT) {
      if (!clientName.trim() || !clientEmail.trim() || !clientPhone.trim()) return errorToast(t('f_mgmtFillDetails'));
      if (!agreed) return errorToast(t('f_mgmtMustAgree'));
      if (signature.trim().toLowerCase() !== clientName.trim().toLowerCase()) return errorToast(t('f_mgmtSignatureMismatch'));
    }
    setStep(step + 1);
  };

  const buildPayload = () => ({
    property_management: {
      plan,
      bill_payment_addon: Boolean(selectedPlan?.allowsBillAddon && billAddon),
      // Sent for the record only - the backend recomputes both from api_configs
      // and stores its own figures, so never display these back as the truth.
      monthly_amount: monthlyDue.toFixed(2),
      payment_amount: monthlyDue.toFixed(2),
      agreement_accepted: agreed,
      agreement_signature: signature.trim(),
      agreement_version: AGREEMENT_VERSION,
      client_name: clientName.trim(),
      client_email: clientEmail.trim(),
      client_phone: clientPhone.trim(),
      property_address: propertyAddress,
      yard_care_frequency: yardFrequency,
      yard_care_notes: yardNotes.trim(),
      inspection_frequency: inspectionFrequency,
      inspection_preferred_day: inspectionDay,
      bills_to_pay: bills,
      bills_other: billsOther.trim(),
      billing_cycle: billingCycle,
      emergency_contact_name: emergencyName.trim(),
      emergency_contact_phone: emergencyPhone.trim(),
      use_approved_vendors: approvedVendors,
      access_method: accessMethod,
      access_detail: accessDetail.trim(),
      url_success: `${window.location.origin}/payment-completion`,
      url_cancel: `${window.location.origin}/payment-completion`
    }
  });

  const handleActivate = () => {
    setSubmitting(true);
    handleLoader(true);

    newPropertyManagement(property.id, buildPayload())
      .then(response => {
        if (response.success) {
          // Anything owing is settled on the gateway's hosted page, which cannot
          // be iframed, so this is a full top level navigation.
          if (response.record?.redirect_url || response.redirect_url) {
            window.location.href = response.record?.redirect_url || response.redirect_url;
            return;
          }

          setEnrolment(response.record || null);
          setWizardOpen(false);
          setStep(STEP_PLAN);
          Swal.fire({
            position: 'middle',
            icon: 'success',
            title: t('f_mgmtActivated'),
            text: response.message || '',
            showConfirmButton: true
          });
        }
        if (!response.success) errorToast(response.errors);
        setSubmitting(false);
        handleLoader(false);
      });
  };

  // Nothing charges on a schedule, so the owner starts each period themselves.
  // Plan and amount come off the existing enrolment - only the return URLs go up.
  const handleRenew = () => {
    setSubmitting(true);
    handleLoader(true);

    renewPropertyManagement(property.id, {
      property_management: {
        url_success: `${window.location.origin}/payment-completion`,
        url_cancel: `${window.location.origin}/payment-completion`
      }
    })
      .then(response => {
        if (response.success) {
          const redirect = response.record?.redirect_url || response.redirect_url;
          if (redirect) {
            window.location.href = redirect;
            return;
          }
          setEnrolment(response.record || null);
        }
        if (!response.success) errorToast(response.errors);
        setSubmitting(false);
        handleLoader(false);
      });
  };

  const handleCancel = async () => {
    const result = await Swal.fire({
      icon: 'warning',
      title: t('f_mgmtCancelTitle'),
      text: t('f_mgmtCancelNotice'),
      showCancelButton: true,
      confirmButtonText: t('f_mgmtCancelConfirm'),
      confirmButtonColor: '#a23131',
      cancelButtonText: t('f_cancel')
    });
    if (!result.isConfirmed) return;

    handleLoader(true);
    cancelPropertyManagement(property.id)
      .then(response => {
        if (response.success) {
          // The row survives the notice period, so keep showing it with the end
          // date rather than clearing it and offering to enrol again (a 422).
          setEnrolment(response.record || null);
          Swal.fire({ position: 'top-end', toast: true, icon: 'success',
                      title: response.message || t('f_mgmtCancelled'),
                      showConfirmButton: false, timer: 3500 });
        }
        if (!response.success) errorToast(response.errors);
        handleLoader(false);
      });
  };

  const money = value => `$${Number(value || 0).toFixed(2)}`;

  const renderPlanCard = planOption => {
    const base = priceOf(planOption.monthlyCostField, planOption.monthlyCostFallback);
    const chosen = plan === planOption.key;

    return (
      <div key={planOption.key} className={`mgmt-plan ${chosen ? 'mgmt-plan-chosen' : ''}`}>
        <h3 className='mb-1'>{planOption.name}</h3>
        <span className='form-sub-text text-grey'>{planOption.audience}</span>
        <p className='mt-1 mb-1'>{planOption.summary}</p>

        <div className='mb-1'>
          <span className='mgmt-price'>{base > 0 ? money(base) : t('f_mgmtNoMonthlyFee')}</span>
          {base > 0 && <span className='text-grey'> / {planOption.monthlyCostUnit}</span>}
        </div>

        <ul className='mgmt-service-list'>
          {planOption.services.map(service => (
            <li key={service}>
              <img src={Checkmark} style={{width: '13px', marginRight: '6px'}} alt='' />
              {service}
            </li>
          ))}
        </ul>

        <div className='mgmt-usage'>
          <span className='form-sub-text text-grey'>{t('f_mgmtUsageFees')}</span>
          {planOption.usageFees.map(fee => (
            <div key={fee.label} className='flex-row justify-flex-between'>
              <span>{fee.label}</span>
              <span><b>{money(priceOf(fee.field, fee.fallback))}</b> / {fee.unit}</span>
            </div>
          ))}
        </div>

        {planOption.allowsBillAddon && chosen && (
          <div className='mt-1'>
            <Checkbox
              name={'bill payment add-on'}
              desc={`${t('f_mgmtBillAddon')} (${money(priceOf(planOption.billAddonField, planOption.billAddonFallback))} / mo)`}
              checkValue={billAddon}
              setCheckValue={setBillAddon}
              handleCheckbox={(state, setState) => setState(!state)}
            />
          </div>
        )}

        <button
          type='button'
          className={`mgmt-plan-btn ${chosen ? 'mgmt-plan-btn-on' : ''}`}
          aria-pressed={chosen}
          onClick={() => { setPlan(planOption.key); if (!planOption.allowsBillAddon) setBillAddon(false); }}
        >
          {chosen ? `✓ ${t('f_mgmtSelected')}` : t('f_mgmtChoosePlan')}
        </button>
      </div>
    );
  };

  const radioRow = (label, options, value, setValue, name) => (
    <div className='mb-2'>
      <span className='form-sub-text'>{label}</span>
      <div className='flex-row'>
        {options.map(option => (
          <button
            key={option}
            type='button'
            className={`mgmt-choice text-camel ${value === option ? 'mgmt-choice-on' : ''}`}
            aria-pressed={value === option}
            onClick={() => setValue(option)}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );

  const summaryRow = (label, value) => (
    <div className='flex-row justify-flex-between mgmt-summary-row'>
      <span>{label}</span>
      <span className='text-camel'><b>{value || '-'}</b></span>
    </div>
  );

  const statusLabel = status => {
    if (status === 'pending') return t('f_mgmtStatusPending');
    if (status === 'pending_cancellation') return t('f_mgmtStatusCancelling');
    if (status === 'cancelled') return t('f_mgmtStatusCancelled');

    return t('f_mgmtActive');
  };

  const renderPayments = payments => (
    <div className='mt-2'>
      <span className='form-sub-text'>{t('f_mgmtPayments')}</span>
      {payments.map(payment => (
        <div key={payment.id} className='flex-row justify-flex-between mgmt-summary-row'>
          <span>
            {payment.kind === 'renewal' ? t('f_mgmtPaymentRenewal') : t('f_mgmtPaymentInitial')}
            {payment.period_start && <span className='text-grey'> {payment.period_start} - {payment.period_end}</span>}
          </span>
          <span>
            <b>{money(payment.amount)}</b>
            <span className={payment.status === 'paid' ? 'text-status ml-1' : 'text-status-gray ml-1'}>
              {payment.status}
            </span>
          </span>
        </div>
      ))}
    </div>
  );

  // Already enrolled - show the standing arrangement instead of the wizard. A
  // cancelled row is treated as no enrolment: the notice period has lapsed and
  // the property is free to be signed up again.
  if (enrolment && enrolment.status !== 'cancelled') {
    const activePlan = planByKey(enrolment.plan);
    const cancelling = enrolment.status === 'pending_cancellation';
    // A $0 plan is never chargeable, so it has nothing to renew
    const chargeable = enrolment.chargeable !== false;
    const canRenew = chargeable && enrolment.renewal_due && !cancelling;

    return (
      <div className='mgmt-root'>
        <h3 className='mb-1'>{t('f_mgmtTitle')}</h3>

        {canRenew && (
          <div className='mgmt-renew mb-2'>
            <div>
              <b>{t('f_mgmtRenewalDue')}</b>
              <div className='form-sub-text text-grey'>{t('f_mgmtNoAutoRenew')}</div>
            </div>
            <button type='button' className='mgmt-btn' disabled={submitting} onClick={handleRenew}>
              {t('f_mgmtRenewNow')}
            </button>
          </div>
        )}

        <div className='mgmt-active'>
          <div className='flex-row justify-flex-between mb-1'>
            <h4>{activePlan?.name || enrolment.plan}</h4>
            <span className={cancelling ? 'text-status-gray' : 'text-status'}>{statusLabel(enrolment.status)}</span>
          </div>

          {summaryRow(t('f_mgmtMonthlyFee'), chargeable ? money(enrolment.monthly_amount) : t('f_mgmtNoMonthlyFee'))}
          {enrolment.bill_payment_addon && summaryRow(t('f_mgmtBillAddon'), t('f_yes'))}
          {chargeable && enrolment.paid_through && summaryRow(t('f_mgmtCoveredUntil'), enrolment.paid_through)}
          {cancelling && enrolment.ends_at && summaryRow(t('f_mgmtEndsOn'), enrolment.ends_at)}

          {enrolment.payments?.length > 0 && renderPayments(enrolment.payments)}

          {chargeable
            ? <span className='form-sub-text text-grey mt-2'>{t('f_mgmtNoAutoRenew')}</span>
            : <span className='form-sub-text text-grey mt-2'>{t('f_mgmtNoRenewalNeeded')}</span>}

          {cancelling ? (
            <span className='form-sub-text text-grey mt-1'>{t('f_mgmtCancelNotice')}</span>
          ) : (
            <>
              <button type='button' className='mgmt-btn-danger mt-2' onClick={handleCancel}>
                {t('f_mgmtCancelPlan')}
              </button>
              <span className='form-sub-text text-grey mt-1'>{t('f_mgmtCancelNotice')}</span>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!wizardOpen) {
    return (
      <div className='mgmt-root'>
        <h3 className='mb-1'>{t('f_mgmtTitle')}</h3>
        <div className='mgmt-intro'>
          <p className='mb-1'>{t('f_mgmtPitch')}</p>
          <span className='form-sub-text text-grey'>{t('f_mgmtPerProperty')}</span>
          <button type='button' className='mgmt-enable' onClick={() => setWizardOpen(true)}>
            <h3>{t('f_mgmtEnable')}</h3>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='mgmt-root'>
      <h3 className='mb-1'>{t('f_mgmtTitle')}</h3>

      <div className='flex-row mgmt-steps mb-2'>
        {[[STEP_PLAN, t('f_mgmtStepPlan')],
          [STEP_AGREEMENT, t('f_mgmtStepAgreement')],
          [STEP_CARE, t('f_mgmtStepCare')],
          [STEP_REVIEW, t('f_confirmation')]].map(([key, label]) => (
            <span key={key} className={`mgmt-step ${step === key ? 'mgmt-step-on' : ''}`}>{label}</span>
        ))}
      </div>

      {step === STEP_PLAN && (
        <div>
          <div className='mgmt-plan-grid'>
            {MANAGEMENT_PLANS.map(renderPlanCard)}
          </div>
          <span className='form-sub-text text-grey'>{t('f_mgmtUsageNote')}</span>
        </div>
      )}

      {step === STEP_AGREEMENT && (
        <div>
          <div className='flex-col mb-2'>
            <span className='form-sub-text'>{t('f_mgmtClientName')}</span>
            <input type='text' className='form-control' value={clientName} onChange={e => setClientName(e.target.value)} />
            <span className='form-sub-text'>{t('g_propertyAddress')}</span>
            <input type='text' className='form-control' value={propertyAddress} readOnly />
            <span className='form-sub-text'>{t('f_email')}</span>
            <input type='email' className='form-control' value={clientEmail} onChange={e => setClientEmail(e.target.value)} />
            <span className='form-sub-text'>{t('f_phone')}</span>
            <input type='text' className='form-control' value={clientPhone} onChange={e => setClientPhone(e.target.value)} />
          </div>

          <h4 className='mb-1'>{t('f_mgmtAgreementTitle')}</h4>
          <div className='mgmt-agreement'>
            {AGREEMENT_SECTIONS.map(section => (
              <div key={section.heading} className='mb-2'>
                <h5>{section.heading}</h5>
                {section.body.map((para, index) => <p key={index}>{para}</p>)}
              </div>
            ))}
          </div>

          <div className='mt-2'>
            <span className='form-sub-text'>{t('f_mgmtSignature')}</span>
            <span className='form-sub-text text-grey'>{t('f_mgmtSignatureHint')}</span>
            <input type='text' className='form-control' value={signature} onChange={e => setSignature(e.target.value)} />
            <div className='flex-row mt-1'>
              <Checkbox
                name={'agree to terms'}
                desc={t('f_mgmtAgreeBox')}
                checkValue={agreed}
                setCheckValue={setAgreed}
                handleCheckbox={(state, setState) => setState(!state)}
              />
            </div>
          </div>
        </div>
      )}

      {step === STEP_CARE && (
        <div>
          <h4 className='mb-2'>{t('f_mgmtCareTitle')}</h4>

          {radioRow(t('f_mgmtYardCare'), YARD_FREQUENCIES, yardFrequency, setYardFrequency)}
          <span className='form-sub-text'>{t('f_mgmtNotes')}</span>
          <input type='text' className='form-control mb-2' value={yardNotes} onChange={e => setYardNotes(e.target.value)} />

          {radioRow(t('f_mgmtInspections'), INSPECTION_FREQUENCIES, inspectionFrequency, setInspectionFrequency)}
          {radioRow(t('f_mgmtPreferredDay'), INSPECTION_DAYS, inspectionDay, setInspectionDay)}

          <div className='mb-2'>
            <span className='form-sub-text'>{t('f_mgmtWhichBills')}</span>
            <div className='flex-row'>
              {BILL_TYPES.map(bill => (
                <button
                  key={bill}
                  type='button'
                  className={`mgmt-choice ${bills.includes(bill) ? 'mgmt-choice-on' : ''}`}
                  aria-pressed={bills.includes(bill)}
                  onClick={() => toggleBill(bill)}
                >
                  {bill}
                </button>
              ))}
            </div>
            <span className='form-sub-text'>{t('f_mgmtOtherBills')}</span>
            <input type='text' className='form-control' value={billsOther} onChange={e => setBillsOther(e.target.value)} />
          </div>

          {radioRow(t('f_mgmtBillingCycle'), BILLING_CYCLES, billingCycle, setBillingCycle)}

          <div className='flex-col mb-2'>
            <span className='form-sub-text'>{t('f_mgmtEmergencyName')}</span>
            <input type='text' className='form-control' value={emergencyName} onChange={e => setEmergencyName(e.target.value)} />
            <span className='form-sub-text'>{t('f_mgmtEmergencyPhone')}</span>
            <input type='text' className='form-control' value={emergencyPhone} onChange={e => setEmergencyPhone(e.target.value)} />
            <div className='flex-row mt-1'>
              <Checkbox
                name={'approved vendors'}
                desc={t('f_mgmtApprovedVendors')}
                checkValue={approvedVendors}
                setCheckValue={setApprovedVendors}
                handleCheckbox={(state, setState) => setState(!state)}
              />
            </div>
          </div>

          {radioRow(t('f_mgmtAccessMethod'), ACCESS_METHODS, accessMethod, setAccessMethod)}
          {accessMethod !== 'drop off keys' && (
            <>
              <span className='form-sub-text'>{accessMethod === 'lockbox' ? t('f_mgmtLockboxCode') : t('f_mgmtAccessOther')}</span>
              <input type='text' className='form-control mb-2' value={accessDetail} onChange={e => setAccessDetail(e.target.value)} />
            </>
          )}
        </div>
      )}

      {step === STEP_REVIEW && (
        <div>
          <h4 className='mb-2'>{t('f_mgmtReviewTitle')}</h4>
          <div className='mgmt-active mb-2'>
            {summaryRow(t('f_mgmtPlan'), selectedPlan?.name)}
            {selectedPlan?.allowsBillAddon && summaryRow(t('f_mgmtBillAddon'), billAddon ? t('f_yes') : t('f_no'))}
            {summaryRow(t('g_propertyAddress'), propertyAddress)}
            {summaryRow(t('f_mgmtYardCare'), yardFrequency)}
            {summaryRow(t('f_mgmtInspections'), `${inspectionFrequency} (${inspectionDay})`)}
            {summaryRow(t('f_mgmtWhichBills'), bills.concat(billsOther ? [billsOther] : []).join(', '))}
            {summaryRow(t('f_mgmtAccessMethod'), accessDetail ? `${accessMethod} - ${accessDetail}` : accessMethod)}
            {summaryRow(t('f_mgmtSignature'), signature)}
            <div className='flex-row justify-flex-between mgmt-summary-row mgmt-total'>
              <span><b>{t('f_mgmtDueToday')}</b></span>
              <span><b>{money(monthlyDue)}</b></span>
            </div>
          </div>

          <p className='form-sub-text text-grey'>
            {monthlyDue > 0 ? t('f_mgmtRedirectNotice') : t('f_mgmtNothingDueNotice')}
          </p>
          <p className='form-sub-text text-grey'>{t('f_mgmtUsageNote')}</p>
        </div>
      )}

      <div className='flex-row justify-flex-end mt-3'>
        <button
          type='button'
          className='mgmt-btn-ghost mr-auto'
          onClick={() => (step === STEP_PLAN ? setWizardOpen(false) : setStep(step - 1))}
        >
          {step === STEP_PLAN ? t('f_cancel') : t('f_back')}
        </button>
        {step < STEP_REVIEW && (
          <button type='button' className='mgmt-btn' onClick={handleNext}>{t('f_next')}</button>
        )}
        {step === STEP_REVIEW && (
          <button type='button' className='mgmt-btn' disabled={submitting} onClick={handleActivate}>
            {monthlyDue > 0 ? t('f_mgmtProceedPayment') : t('f_mgmtActivate')}
          </button>
        )}
      </div>
    </div>
  );
};

PropertyManagement.propTypes = {
  property: propTypes.instanceOf(Object),
  user: propTypes.instanceOf(Object),
  handleLoader: propTypes.func.isRequired
};

export default PropertyManagement;
